'use client';

import React from 'react';

const featureCards = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    title: 'Car-O-Liner Certified',
    desc: 'Precision chassis alignment to factory tolerance. Structural repairs done right.',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    title: 'Mercedes · BMW · VW Group',
    desc: 'Module coding, programming & SRS system reset for German vehicles.',
  },
];

export default function HeroSection() {
  return (
    <section
      className="relative z-10 min-h-screen bg-[#0a0a0a] flex flex-col"
      aria-label="Hero — Non-fault car repairs"
    >
      {/* Background subtle texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(ellipse 80% 60% at 60% 40%, rgba(30,10,10,0.6) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      {/* Main content area */}
      <div className="flex-1 flex flex-col md:flex-row max-w-8xl mx-auto w-full px-6 md:px-12 pt-32 pb-0">
        {/* Left column */}
        <div className="flex-1 flex flex-col justify-center py-12 md:py-20 pr-0 md:pr-16">
          {/* Badge pill */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-10 border border-[#CC1A1A]/60 bg-transparent w-fit">
            <span className="w-2 h-2 rounded-full bg-[#CC1A1A] inline-block" aria-hidden="true" />
            <span className="text-[11px] font-dm-sans font-semibold tracking-[0.25em] uppercase text-white">
              Non-Fault Accident Repairs
            </span>
          </div>

          {/* Main headline */}
          <h1 className="mb-6">
            <span className="block font-fraunces font-light text-white leading-[1.0]" style={{ fontSize: 'clamp(2.8rem, 6vw, 5rem)' }}>
              Your accident.
            </span>
            <span
              className="block font-fraunces font-light italic leading-[1.0]"
              style={{ fontSize: 'clamp(2.8rem, 6vw, 5rem)', color: '#CC1A1A' }}
            >
              Not your bill.
            </span>
          </h1>

          {/* Subtext */}
          <p className="text-base font-dm-sans font-normal text-white/55 max-w-lg leading-relaxed mb-10">
            If the accident wasn&apos;t your fault, you pay{' '}
            <strong className="text-white font-semibold">absolutely nothing</strong>. We
            handle your insurance claim, repair your car to manufacturer
            standard, and provide a courtesy vehicle — all at zero cost to you.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#CC1A1A] text-white font-dm-sans font-bold text-[13px] uppercase tracking-[0.15em] hover:bg-[#E02020] transition-all duration-200"
            >
              START FREE CLAIM — PAY £0
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <a
              href="tel:01614001687"
              className="inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-[#1a1a1a] border border-white/10 text-white font-dm-sans font-medium text-[13px] tracking-wide hover:bg-[#222] transition-all duration-200"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
              0161 400 1687
            </a>
          </div>

          {/* Stats bar */}
          <div className="flex items-start gap-10 border-t border-white/[0.08] pt-8">
            <div>
              <span className="block text-3xl font-fraunces font-light text-white leading-none mb-1">£0</span>
              <span className="block text-[11px] font-dm-sans font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mb-1">You Pay</span>
              <span className="block text-[11px] font-dm-sans text-white/40">non-fault claims</span>
            </div>
            <div>
              <span className="block text-3xl font-fraunces font-light text-white leading-none mb-1">✓</span>
              <span className="block text-[11px] font-dm-sans font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mb-1">Courtesy Car</span>
              <span className="block text-[11px] font-dm-sans text-white/40">provided free</span>
            </div>
            <div>
              <span className="block text-3xl font-fraunces font-light text-white leading-none mb-1">100%</span>
              <span className="block text-[11px] font-dm-sans font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mb-1">Repairs</span>
              <span className="block text-[11px] font-dm-sans text-white/40">manufacturer standard</span>
            </div>
          </div>
        </div>

        {/* Right column — feature cards + availability */}
        <div className="hidden md:flex flex-col justify-center gap-4 py-20 w-80 lg:w-96 shrink-0">
          {featureCards?.map((card, i) => (
            <div
              key={i}
              className="bg-[#141414] border border-white/[0.07] p-6 flex items-start gap-4"
            >
              <div className="w-10 h-10 bg-[#1e1e1e] flex items-center justify-center shrink-0 text-[#CC1A1A]">
                {card?.icon}
              </div>
              <div>
                <h3 className="font-dm-sans font-semibold text-white text-[14px] mb-1.5">{card?.title}</h3>
                <p className="font-dm-sans text-[12px] text-white/45 leading-relaxed">{card?.desc}</p>
              </div>
            </div>
          ))}

          {/* Availability indicator */}
          <div className="bg-[#141414] border border-white/[0.07] px-5 py-4 flex items-center gap-3 self-end">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0 animate-[green-pulse_2s_ease-in-out_infinite]" aria-hidden="true" />
            <div>
              <span className="block font-dm-sans font-semibold text-white text-[13px]">Accepting claims today</span>
              <span className="block font-dm-sans text-[11px] text-white/40">Mon – Sat · 8AM – 6PM</span>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="flex justify-center pb-8 opacity-30" aria-hidden="true">
        <div className="flex flex-col items-center gap-2">
          <span className="font-dm-sans text-[10px] tracking-[0.3em] uppercase text-white/50">Scroll</span>
          <div className="w-px h-8 bg-white/20" />
        </div>
      </div>
    </section>
  );
}
